import {Button} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {LockLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {WorkstreamBasic, WorkstreamPrivacy} from 'client';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {Link} from 'components/DSYS/Link';
import {SkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {StatusButton} from 'components/DSYS/StatusButton';
import {Title} from 'components/DSYS/Title';
import styles from 'components/profile/MyWork.module.css';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useMemo, useState} from 'react';
import {
  getStatusPriority,
  getWorkstreamDri,
  getWorkstreamUpdateVisualStatus,
  isWorkstreamStatusArchived,
} from 'views/workstreams/utils';
import {WorkstreamStatusUpdateModal} from 'views/workstreams/WorkstreamStatusUpdateModal';

import {useTeamWorkstreams} from './hooks';

const limitWorkstreams = 9;

export const TeamWorkstreamsCard = ({teamSlug}: {teamSlug: string}) => {
  const {data: workstreams, isLoading} = useTeamWorkstreams({slug: teamSlug});
  const [expanded, setExpanded] = useState(false);
  const [updateWorkstream, setUpdateWorkstream] = useState<WorkstreamBasic | undefined>(undefined);

  const visibleWorkstreams = useMemo(
    () =>
      [...workstreams]
        .filter((workstream) => !workstream.archived && !isWorkstreamStatusArchived(workstream))
        .sort((left, right) => {
          const leftPriority = getStatusPriority(left.updates?.[0]?.status);
          const rightPriority = getStatusPriority(right.updates?.[0]?.status);

          if (leftPriority !== rightPriority) {
            return leftPriority - rightPriority;
          }

          return left.id - right.id;
        }),
    [workstreams]
  );

  const displayedWorkstreams = visibleWorkstreams.slice(0, expanded ? undefined : limitWorkstreams);

  if (!isLoading && visibleWorkstreams.length === 0) {
    return null;
  }

  return (
    <Stack
      width="100%"
      className={atoms({
        paddingX: '20',
        paddingY: '16',
        borderWidth: '1',
        borderColor: 'Border Subtle',
        borderRadius: 'Medium',
        borderStyle: 'Solid',
      })}
    >
      <Split>
        <Split.Item width="fill">
          <Title size={16} withAccessoryStart={<UIIcon src={WorkstreamsLine} isOpticallyAligned />}>
            {t('workstreams')}
          </Title>
        </Split.Item>
      </Split>

      <SkeletonFadeIn loading={isLoading} skeleton={<TeamWorkstreamsSkeleton />}>
        {displayedWorkstreams.map((workstream) => {
          const dri = getWorkstreamDri(workstream);

          return (
            <Box
              key={workstream.id}
              padding="8"
              {...withShade({duration: 'Instant'})}
              borderRadius="Medium"
            >
              <Link
                to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', workstream.id.toString())}
                state={{source: 'teams'}}
                hasNoUnderline
                className={styles.workstreamLink}
              >
                <Box display="flex" justifyContent="space-between" className={styles.workstreamRow}>
                  <Box flexGrow={1} style={{minWidth: 0}}>
                    <LabelGroup
                      align="top"
                      withText={<Text isBold>{workstream.name}</Text>}
                      withSubtext={
                        <Split gap="8" alignY="center">
                          {dri && (
                            <Split.Item>
                              <Text size="small" color="subtle">
                                {dri.name}
                              </Text>
                            </Split.Item>
                          )}

                          {dri && workstream.privacy !== WorkstreamPrivacy.PUBLIC && (
                            <Split.Item>
                              <Box backgroundColor="Border Subtle" style={{height: 12, width: 1}} />
                            </Split.Item>
                          )}

                          {workstream.privacy !== WorkstreamPrivacy.PUBLIC && (
                            <Split.Item>
                              <Box display="flex" alignItems="center" style={{gap: 4}}>
                                <UIIcon src={LockLine} size="small" /> {t('private')}
                              </Box>
                            </Split.Item>
                          )}

                          {workstream.privacy === WorkstreamPrivacy.CUSTOM && (
                            <Split.Item>
                              <Chip size="small">
                                <Chip.Content>{t('custom')}</Chip.Content>
                              </Chip>
                            </Split.Item>
                          )}
                        </Split>
                      }
                    />
                  </Box>
                  <Box flexShrink={0}>
                    <StatusButton
                      status={getWorkstreamUpdateVisualStatus(workstream.updates?.[0]?.status)}
                      onClick={(event: React.SyntheticEvent) => {
                        event.preventDefault();
                        setUpdateWorkstream(workstream);
                      }}
                      includeArchived
                    />
                  </Box>
                </Box>
              </Link>
            </Box>
          );
        })}

        {visibleWorkstreams.length > limitWorkstreams && (
          <Box paddingTop="12">
            <Button
              variant="transparent"
              size="small"
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded
                ? t('show_less')
                : t('show_more_count', {count: visibleWorkstreams.length - limitWorkstreams})}
            </Button>
          </Box>
        )}

        <WorkstreamStatusUpdateModal
          workstreamId={updateWorkstream?.id}
          onClose={() => setUpdateWorkstream(undefined)}
          source="teams"
        />
      </SkeletonFadeIn>
    </Stack>
  );
};

const TeamWorkstreamsSkeleton = () => (
  <Box display="flex" flexDirection="column">
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius="medium" />
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius="medium" />
    <Skeleton.Box height={44} className={atoms({marginY: '8'})} withBorderRadius="medium" />
  </Box>
);
